// frontend-website/src/telemetry/eventQueue.js
// Event queue for batching telemetry events

/**
 * EventQueue - Batches events and sends them in groups to reduce API calls
 */
export class EventQueue {
    constructor(batchSize = 10, flushInterval = 5000, sendCallback) {
        this.batchSize = batchSize;
        this.flushInterval = flushInterval;
        this.sendCallback = sendCallback;
        this.queue = [];
        this.flushTimer = null;
        this.isFlushing = false;

        // Start periodic flush
        this._startFlushTimer();
    }

    /**
     * Add an event to the queue
     */
    add(event) {
        this.queue.push(event);

        // Auto-flush if batch size reached
        if (this.queue.length >= this.batchSize) {
            this.flush();
        }
    }

    /**
     * Flush all events asynchronously
     */
    async flush() {
        if (this.isFlushing || this.queue.length === 0) {
            return;
        }

        this.isFlushing = true;

        // Get events to send
        const eventsToSend = [...this.queue];
        this.queue = [];

        // Send events individually (backend expects single events, not batches)
        try {
            // Send each event individually
            const promises = eventsToSend.map(event => this.sendCallback(event));
            await Promise.all(promises);
        } catch (error) {
            // On error, put events back in queue
            console.error('[EventQueue] Flush failed:', error);
            this.queue = [...eventsToSend, ...this.queue];
        } finally {
            this.isFlushing = false;
        }
    }

    /**
     * Flush synchronously using sendBeacon (for page unload)
     */
    flushSync() {
        if (this.queue.length === 0) {
            return;
        }

        // Use sendBeacon for reliable delivery during page unload
        const eventsToSend = [...this.queue];
        this.queue = [];

        // Send each event individually using sendBeacon
        for (const event of eventsToSend) {
            try {
                const blob = new Blob([JSON.stringify(event)], {
                    type: 'application/json'
                });

                // Extract endpoint URL from sendCallback
                // Fallback to default endpoint
                const endpoint = '/api/telemetry/track';

                navigator.sendBeacon(endpoint, blob);
            } catch (error) {
                console.error('[EventQueue] SendBeacon failed:', error);
            }
        }
    }

    /**
     * Start periodic flush timer
     * @private
     */
    _startFlushTimer() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }

        this.flushTimer = setInterval(() => {
            if (this.queue.length > 0) {
                this.flush();
            }
        }, this.flushInterval);
    }

    /**
     * Stop flush timer
     */
    stop() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }

        // Flush any remaining events
        this.flush();
    }
}
